import { Metamodel } from '@ardoq/api-types';
import {
  MetamodelContextSettings,
  MetamodelReferenceCompletenessContext,
} from 'metamodel/types';
import { DisplayPreference } from './types';
import {
  getContextComponentType,
  isOutgoingDirection,
  getOtherComponentType,
} from './utils';
import { WorkspaceNavigationTitle } from '../workspaceNavigationTitle/WorkspaceNavigationTitle';
import { InlineText, Paragraph } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import { Trans, type I18nLocale } from '@ardoq/i18n';

export const ReferenceCompletionTitle = ({
  metamodel,
  context,
  i18nLocale,
}: {
  metamodel: Metamodel;
  context: MetamodelReferenceCompletenessContext & MetamodelContextSettings;
  i18nLocale: I18nLocale;
}) => {
  const contextComponentTypeName = getContextComponentType(
    metamodel,
    context
  ).name;
  const otherComponentTypeName = getOtherComponentType(metamodel, context).name;
  const referenceTypeName = context.name;
  const { contextComponentTypeIsSource } = context;
  return (
    <WorkspaceNavigationTitle
      metamodel={metamodel}
      context={context}
      i18nLocale={i18nLocale}
    >
      {contextComponentTypeIsSource ? (
        <>
          <InlineText>{contextComponentTypeName}</InlineText> ›{' '}
          {referenceTypeName} › {otherComponentTypeName}
        </>
      ) : (
        <>
          {otherComponentTypeName} › {referenceTypeName} ›{' '}
          <InlineText>{contextComponentTypeName}</InlineText>
        </>
      )}
    </WorkspaceNavigationTitle>
  );
};

type ReferenceCompletionExplanationProps = {
  metamodel: Metamodel;
  context: MetamodelReferenceCompletenessContext;
  displayPreference: DisplayPreference;
};
const EXPLANATION_KEYS = {
  outgoingHave: {
    key: 'metamodel:stats.referenceCompletion.explanation.outgoingHave',
    defaults:
      'The following components of type {{contextComponentTypeName}} have an outgoing reference of type <italic></italic> to {{otherComponentTypeName}}',
  },
  outgoingDoNotHave: {
    key: 'metamodel:stats.referenceCompletion.explanation.outgoingDoNotHave',
    defaults:
      'The following components of type {{contextComponentTypeName}} do not have an outgoing reference of type <italic></italic> to {{otherComponentTypeName}}',
  },
  incomingHave: {
    key: 'metamodel:stats.referenceCompletion.explanation.incomingHave',
    defaults:
      'The following components of type {{contextComponentTypeName}} have an incoming reference of type <italic></italic> from {{otherComponentTypeName}}',
  },
  incomingDoNotHave: {
    key: 'metamodel:stats.referenceCompletion.explanation.incomingDoNotHave',
    defaults:
      'The following components of type {{contextComponentTypeName}} do not have an incoming reference of type <italic></italic> from {{otherComponentTypeName}}',
  },
} as const;

export const ReferenceCompletionExplanation = ({
  metamodel,
  context,
  displayPreference,
}: ReferenceCompletionExplanationProps) => {
  const contextComponentTypeName = getContextComponentType(
    metamodel,
    context
  ).name;
  const otherComponentTypeName = getOtherComponentType(metamodel, context).name;
  const isOutgoing = isOutgoingDirection(context);
  const referenceTypeName = context.name;
  const isWithReference =
    displayPreference === DisplayPreference.WITH_REFERENCE;

  // Four fully self-contained explanation sentences — one per
  // (outgoing/incoming × have/do-not-have) branch — so each locale
  // controls word order. The reference-type name is rendered in
  // italics via the <italic> slot in the Trans component.
  const branch = isOutgoing
    ? isWithReference
      ? EXPLANATION_KEYS.outgoingHave
      : EXPLANATION_KEYS.outgoingDoNotHave
    : isWithReference
      ? EXPLANATION_KEYS.incomingHave
      : EXPLANATION_KEYS.incomingDoNotHave;

  return (
    <Box marginTop="medium">
      <Paragraph variant="text1">
        <Trans
          i18nKey={branch.key}
          values={{ contextComponentTypeName, otherComponentTypeName }}
          defaults={branch.defaults}
          components={{
            italic: (
              <InlineText variant="text1Italic">{referenceTypeName}</InlineText>
            ),
          }}
        />
      </Paragraph>
    </Box>
  );
};
